import {
  AddUserResponseType,
  AddUserType,
  GetUserParamsType,
  GetUserResponseType,
  UserPermissions,
  UserRolesResponse,
  UserType,
} from '@consolidocs-ui/types';
import { enhanceEndpoints } from '../core';

/**
 * Adding typing for tags for Usersapi
 */
const UserApiWithTag = enhanceEndpoints({
  addTagTypes: [
    'users',
    'user-details',
    'user-roles',
    'user-permissions',
    'user-profile-picture',
    'dashboard-config-user',
    'general-configuration',
    'organization-configuration',
  ],
});

// User API Services
export const UserApi = UserApiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<UserType, void>({
      query: () => ({
        url: 'whoami',
      }),
    }),
    getUsers: builder.query<
      GetUserResponseType,
      Partial<GetUserParamsType | void>
    >({
      query: (params) => ({
        url: 'users',
        params: {
          page: 1,
          page_size: 1000,
          ...params,
        },
      }),
      providesTags: ['users'],
    }),
    getUserById: builder.query<{ data: UserType }, string>({
      query: (id) => ({
        url: `users/${id}/`,
      }),
      providesTags: ['user-details'],
    }),
    createUser: builder.mutation<AddUserResponseType, AddUserType>({
      query: (user: AddUserType) => {
        return {
          url: `users/create`,
          method: 'POST',
          data: user,
        };
      },
      invalidatesTags: ['users'],
    }),
    updateUser: builder.mutation<UserType, Partial<UserType>>({
      query: (user: Partial<UserType>) => {
        const { id, ...updatedUser } = user as {
          id: string;
          updatedUser: Partial<UserType>;
        };
        return {
          url: `users/${id}`,
          method: 'PUT',
          body: updatedUser,
        };
      },
      invalidatesTags: ['users'],
    }),
    deactiveUser: builder.mutation<UserType, Partial<UserType>>({
      query: (user: Partial<UserType>) => {
        const { email } = user;
        return {
          url: `users/status`,
          method: 'POST',
          data: {
            status: 'disabled',
            email,
          },
        };
      },
      invalidatesTags: ['users'],
    }),
    activateUser: builder.mutation<UserType, Partial<UserType>>({
      query: (user: Partial<UserType>) => {
        const { email } = user;
        return {
          url: `users/status`,
          method: 'POST',
          data: {
            status: 'enabled',
            email,
          },
        };
      },
      invalidatesTags: ['users'],
    }),
    // Hard delete from Cognito and the backend database; company admins only.
    // Takes the user's idx, not their Cognito username: the two differ for
    // Google sign-in users ("google_<id>" in Cognito).
    deleteUser: builder.mutation<void, string>({
      query: (idx) => ({
        url: 'users/delete',
        method: 'POST',
        data: { idx },
      }),
      invalidatesTags: ['users'],
    }),
    getPermissions: builder.query<UserPermissions, string>({
      query: () => ({
        url: `debug/permissions`,
      }),
      transformResponse: (response: { data: UserPermissions }) => response.data,
      providesTags: ['user-permissions'],
    }),
    getUserRolesByUserId: builder.query<UserRolesResponse, string>({
      query: (userId) => ({
        url: `user-roles/`,
        params: {
          user_id: userId,
        },
      }),
      providesTags: ['user-roles'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: 'logout',
      }),
    }),
    versionTest: builder.query<
      {
        data: {
          version_info: {
            commit_id: string;
          };
        };
      },
      string
    >({
      query: () => ({
        url: `version-test/`,
      }),
    }),
    updatePersonalizedDashboard: builder.mutation<
      { success: boolean },
      { personalized_dashboard: boolean; show_summary_files: boolean }
    >({
      query: (config) => ({
        url: `user/dashboard-preference/`, // Dummy URL for now
        method: 'PUT',
        data: config,
      }),
      // Invalidate permissions to refetch updated config
      invalidatesTags: () => [
        { type: 'users', id: 'permissions' },
        'user-permissions',
        'dashboard-config-user',
      ],
      // Optimistically update the cache
      async onQueryStarted(config, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          UserApi.util.updateQueryData('getPermissions', '', (draft) => {
            if (draft) {
              draft.personalized_dashboard = config.personalized_dashboard;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateProfile: builder.mutation<
      { success: boolean },
      Partial<{
        first_name: string;
        last_name: string;
        password: string;
        user_language_preference: string;
        userId?: string;
      }>
    >({
      query: ({ userId, ...data }) => ({
        url: 'user/update-profile/',
        method: 'PATCH',
        data: userId ? { ...data, user_id: userId } : data,
      }),
      invalidatesTags: ['users', 'user-details'],
    }),
    getProfilePicture: builder.query<{ data: { url: string } }, string | void>({
      query: (userId) => ({
        url: 'user/profile-picture/',
        method: 'GET',
        params: userId ? { user_id: userId } : undefined,
      }),
      providesTags: ['user-profile-picture'],
    }),
    uploadProfilePicture: builder.mutation<
      { success: boolean; url: string },
      { formData: FormData; userId?: string }
    >({
      query: ({ formData, userId }) => {
        if (userId) {
          formData.append('user_id', userId);
        }
        return {
          url: 'user/profile-picture/',
          method: 'PUT',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
      },
      invalidatesTags: ['user-profile-picture'],
    }),
    resendTempPassword: builder.mutation<
      { status_message?: string },
      { email: string }
    >({
      query: ({ email }) => ({
        url: 'user/resend-temp-password',
        method: 'POST',
        data: { email },
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useDeactiveUserMutation,
  useActivateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetPermissionsQuery,
  useGetUserRolesByUserIdQuery,
  useLogoutMutation,
  useVersionTestQuery,
  useUpdatePersonalizedDashboardMutation,
  useUpdateProfileMutation,
  useGetProfilePictureQuery,
  useUploadProfilePictureMutation,
  useResendTempPasswordMutation,
} = UserApi;
